import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { Context } from "../context/context";
import HeaderLogo from "./header-logo";
import ChatMessages from "../components/chat-messages";
import ChatInput from "../components/chat-input";
import FeedbackDialog from "../components/feedback-dialog";
import useAPI from "../hooks/use-api";
import config from "../config.json";

const ChatArea = (props) => {
  const theme = useTheme();

  const {
    conversationIdContext,
    chatInputContext,
    errorStateContext,
    loadingContext,
    messagesContext,
    previewDocumentContext,
    welcomeQuestionsContext,
  } = useContext(Context);

  const [messageList, setMessageList] = messagesContext;
  const [errorState, setErrorState] = errorStateContext;
  const [, setLoading] = loadingContext;
  const [conversationId, setConversationId] = conversationIdContext;
  const [, setChatInput] = chatInputContext;
  const [, setWelcomeQuestions] = welcomeQuestionsContext;
  const [, setPreviewDocument] = previewDocumentContext;

  const { api } = useAPI();
  const [useStream, setUseStream] = useState(true);
  const [cachedContext, setCachedContext] = useState({}); // New state for cached_context

  const addOrReplacePendingMessage = useCallback(
    (message, forceAdd = true, streamedMessage = false) => {
      setMessageList((prevMessageList) => {
        if (
          (!forceAdd && prevMessageList[prevMessageList.length - 1]?.pending) ||
          streamedMessage
        ) {
          // Replace the last message if it's pending or streamed
          return prevMessageList.map((item, index) =>
            index === prevMessageList.length - 1 ? message : item
          );
        } else {
          // Append a new message
          return [...prevMessageList, message];
        }
      });
    },
    [setMessageList]
  );

  const addUserMessage = useCallback(
    (message) => {
      return addOrReplacePendingMessage({ ...message, role: "user" });
    },
    [addOrReplacePendingMessage]
  );

  const addAgentMessage = useCallback(
    (message) => {
      return addOrReplacePendingMessage(
        { ...message, role: "assistant" },
        false
      );
    },
    [addOrReplacePendingMessage]
  );

  const streamAgentMessage = useCallback(
    (message) => {
      return addOrReplacePendingMessage(
        { ...message, role: "assistant" },
        false,
        true
      );
    },
    [addOrReplacePendingMessage]
  );

  const sendMessageAndUpdateChat = async (input = null) => {
    const getHistoryFromMessageList = (messageList) => {
      return messageList.map((message) => ({
        role: message.role,
        content: message.content,
        source: message.source,
      }));
    };

    if (!input || input === "") {
      return;
    }

    setLoading(true);
    setPreviewDocument(null);
    setWelcomeQuestions([]);
    setChatInput("");

    // Add the user message
    addUserMessage({ content: input });

    // Add a pending assistant message
    addAgentMessage({ content: "...", source: "misc", pending: true });

    let response;
    let agentMessage = {
      role: "assistant",
      reaction: 0,
      comment: "",
      enableFeedback: false,
    };

    if (useStream) {
      try {
        response = await api.post(
          "/chat_stream",
          {
            conversation_id: conversationId,
            approach: config.APPROACH,
            history: [
              ...getHistoryFromMessageList(messageList),
              { role: "user", content: input },
            ],
            cached_context: cachedContext, // Include cached_context in the request payload
          },
          {
            responseType: "stream",
            adapter: "fetch",
          }
        );

        const reader = response.data.getReader();
        const decoder = new TextDecoder("utf-8");
        let streamedContent = "";

        // Continuously read streamed chunks
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const data = decoder.decode(value);
          if (data.startsWith("data:")) {
            const datasegments = data.split("data: ");
            for (let datasegment of datasegments) {
              datasegment = datasegment.trim();
              if (!datasegment) continue;
              try {
                const metadata = JSON.parse(datasegment);
                  
                // Append chunk to the content
                streamedContent += metadata.chunk;

                // Update agent message dynamically
                streamAgentMessage({
                  ...agentMessage,
                  enableFeedback: true,
                  content: streamedContent || "",
                  data_points: metadata.data_points || [],
                  conversationId: metadata.conversation_id || -1,
                  citations: metadata.citations || [],
                });

                setConversationId(metadata.conversation_id);

                // Update cached_context state with the new data
                if (metadata.cache) {
                  setCachedContext(metadata.cache);
                }
              } catch (error) {
                console.error("Error during streaming:", error, datasegment);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error during streaming:", error);

        addAgentMessage({
          ...agentMessage,
          content: "[Error occurred while streaming]",
          pending: false,
          source: "misc",
        });
      }
    }
   else {
      try {
        response = await api.post("/chat", {
          conversation_id: conversationId,
          approach: config.APPROACH,
          history: [
            ...getHistoryFromMessageList(messageList),
            { role: "user", content: input },
          ],
        });

        agentMessage = {
          ...agentMessage,
          enableFeedback: true,
          content: response.data?.answer || "",
          data_points: response.data?.data_points || [],
          conversationId: response.data?.conversation_id || -1,
          thoughts: response.data?.thoughts || [],
          citations: response.data?.citations || [],
        };

        setConversationId(response.data?.conversation_id);

        // update dummy agent message with actual response from backend
        addAgentMessage({ ...agentMessage, source: "model" });

        setErrorState({ error: false });
      } catch (error) {
        console.error("Failed to talk to agent. Reason was: " + error);
        setErrorState({ error: true, message: error });
        agentMessage = {
          ...agentMessage,
          content: error,
        };
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!errorState.error) {
      return;
    }
    if (!(errorState.message && errorState.message !== "")) {
      return;
    }

    addAgentMessage({ source: "misc", content: errorState.message });
  }, [errorState, addAgentMessage]);

  return (
    <>
      <div
        id="chat-header"
        style={{
          padding: "10px",
        }}
      >
        <HeaderLogo />
      </div>
      <div
        id="chat-messages"
        style={{
          height: "100%",
          padding: "10px",
          overflow: "auto",
        }}
      >
        <ChatMessages
          error={errorState.error}
          onClickWelcomeQuestion={sendMessageAndUpdateChat}
        />
      </div>
      <div
        id="toggle-container"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px",
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "16px",
            fontWeight: "500",
            color: theme.palette.primary.main, // Use theme primary light for text
          }}
        >
          Use Streaming:
          <div style={{ position: "relative", width: "40px", height: "20px" }}>
            <input
              type="checkbox"
              checked={useStream}
              onChange={(e) => setUseStream(e.target.checked)}
              style={{
                opacity: 0,
                width: 0,
                height: 0,
              }}
            />
            <span
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "40px",
                height: "20px",
                backgroundColor: useStream
                  ? theme.palette.primary.main // Theme primary main for active state
                  : theme.palette.primary.light, // Theme primary light for inactive state
                borderRadius: "20px",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "3px",
                left: useStream ? "22px" : "3px",
                width: "14px",
                height: "14px",
                backgroundColor: "#fff", // The ball remains white
                borderRadius: "50%",
                transition: "left 0.3s",
              }}
            />
          </div>
        </label>
      </div>
      <div
        id="chat-input"
        style={{
          display: "flex",
          flexShrink: 0,
          padding: "10px",
          alignItems: "center",
        }}
      >
        <ChatInput onSubmit={sendMessageAndUpdateChat} />
      </div>
      <FeedbackDialog />
    </>
  );
};

export default ChatArea;